import {apiFetch} from "../api/client";
import {completionLabel, completionListPollMs} from "../components/ExternalCompletion";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ApiError, appealPayment, cancelOrderByTrader, cancelPayment, claimOrder, completePayment, getMyPayments, getRapiraRateInfo, getTeamBalance, sendExchangePayout, setPayinRequisites, uploadReceipt} from "../api";
import {apiErrorCode} from "../api/error-codes";
import type {RapiraRateInfoResponse, TeamBalanceResponse} from "../api";
import {useAuth} from "../auth";
import {PaymentDetailsPanel, PaymentsSplitView} from "../components";
import {EXCHANGE_SIGNAL_POLL_MS, drawerStaysOpen, isActiveTraderPayment, isStalledPayment, shouldPollClientSignals} from "../components/payment-details";
import {usePaymentsSocket} from "../hooks/usePaymentsSocket";
import type {Payment, PaymentActionKind, PaymentActionRequest} from "../types";
import {confirmPayinPart, replacePayinPart} from "../api/merchantPortalPayin";
import {formatAgeSeconds, formatRateMarkup, formatRelativeTime, truncateAmount} from "../utils";
import {mergePaymentSnapshots} from "./paymentSnapshots";

export function PaymentsPage(): JSX.Element {
    const {token} = useAuth();
    const socketPayments = usePaymentsSocket();
    const [myPayments, setMyPayments] = useState<Payment[]>([]);
    const [hiddenOrderIds, setHiddenOrderIds] = useState<Set<string>>(() => new Set());
    const [claimingOrderIds, setClaimingOrderIds] = useState<Set<string>>(() => new Set());
    const [selectedPaymentKey, setSelectedPaymentKey] = useState<string | null>(null);
    const [loadingMyPayments, setLoadingMyPayments] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [balance, setBalance] = useState<TeamBalanceResponse | null>(null);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [balanceError, setBalanceError] = useState("");
    const [balanceMissing, setBalanceMissing] = useState(false);
    const [rapiraRate, setRapiraRate] = useState<RapiraRateInfoResponse | null>(null);
    const [rapiraRateLoading, setRapiraRateLoading] = useState(false);
    const [rapiraRateError, setRapiraRateError] = useState("");

    const refreshMyPayments = useCallback(async () => {
        if (!token) {
            setMyPayments([]);
            return;
        }
        setLoadingMyPayments(true);
        try {
            const items = await getMyPayments(token);
            setMyPayments(previous => mergePaymentSnapshots(previous, items));
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoadingMyPayments(false);
        }
    }, [token]);

    useEffect(() => {
        void refreshMyPayments();
    }, [refreshMyPayments]);

    const refreshBalance = useCallback(async () => {
        if (!token) {
            setBalance(null);
            setBalanceMissing(false);
            setBalanceError("");
            return;
        }
        setBalanceLoading(true);
        setBalanceError("");
        setBalanceMissing(false);
        try {
            setBalance(await getTeamBalance(token));
        } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
                setBalance(null);
                setBalanceMissing(true);
            } else {
                setBalance(null);
                setBalanceMissing(false);
                setBalanceError(formatApiError(err));
            }
        } finally {
            setBalanceLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void refreshBalance();
    }, [refreshBalance]);

    const refreshRapiraRate = useCallback(async () => {
        if (!token) {
            setRapiraRate(null);
            setRapiraRateError("");
            setRapiraRateLoading(false);
            return;
        }
        setRapiraRateLoading(true);
        setRapiraRateError("");
        try {
            setRapiraRate(await getRapiraRateInfo(token));
        } catch (err) {
            setRapiraRateError(formatApiError(err));
        } finally {
            setRapiraRateLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void refreshRapiraRate();
        if (!token) return;
        // The polling period roughly matches the backend cache TTL; polling
        // faster would only return the same Provider cache entry.
        const intervalId = window.setInterval(() => {
            void refreshRapiraRate();
        }, 30_000);
        return () => window.clearInterval(intervalId);
    }, [token, refreshRapiraRate]);

    const activeMyPayments = useMemo(
        () => myPayments.filter(isActiveTraderPayment).sort(compareByOrderAge),
        [myPayments]
    );

    // «В работе» и «встали» — один и тот же список заявок трейдера, разложенный
    // по одному признаку сервера. Заявка, которую площадка закрыла или на
    // которую сторож повесил замок, не исчезает: она остаётся за трейдером и
    // видна ему, но лежит ниже и не мешается среди той работы, которую он
    // обязан довести. Разбирать её будет админ — подметалка уже увела её в спор.
    const workingMyPayments = useMemo(
        () => activeMyPayments.filter((payment) => !isStalledPayment(payment)),
        [activeMyPayments]
    );
    const stalledMyPayments = useMemo(
        () => activeMyPayments.filter(isStalledPayment),
        [activeMyPayments]
    );

    const availablePayments = useMemo(() => {
        // Вычитается весь активный список, а не только «в работе»: встав, заявка
        // из-под трейдера не уходит, и всплыви она в пуле — её предложили бы
        // взять второй раз.
        const activeMyOrderIds = new Set(activeMyPayments.map((p) => p.orderId));
        return socketPayments
            .filter((p) => !hiddenOrderIds.has(p.orderId) && !activeMyOrderIds.has(p.orderId))
            .sort(compareByOrderAge);
    }, [activeMyPayments, hiddenOrderIds, socketPayments]);

    // Ищется по всему активному списку: карточка вставшей заявки открывается —
    // трейдеру надо видеть, чем именно она закончилась. Действий в ней нет, их
    // гасит сама панель по externalClosedAtSource.
    const selectedPayment = useMemo(() => {
        if (!selectedPaymentKey) return null;
        return activeMyPayments.find((payment) => paymentKey(payment) === selectedPaymentKey) ?? null;
    }, [activeMyPayments, selectedPaymentKey]);

    useEffect(() => {
        if (selectedPaymentKey && !selectedPayment) {
            setSelectedPaymentKey(null);
        }
    }, [selectedPayment, selectedPaymentKey]);

    // Опрос сигналов клиента.
    //
    // Сигнал приходит СНАРУЖИ — от клиента через мерчанта, — и канала
    // уведомления у нас для него пока нет: WebSocket публикует свободный пул, а
    // адресного сообщения по взятой заявке не существует. Пока это так, панель
    // покупки перечитывает список «мои заявки» по таймеру, и `selectedPayment`
    // пересчитывается из живого списка сам.
    //
    // Условие ровно одно и проверяется отдельной функцией: опрос живёт, только
    // пока открыта панель обмена. Закрыл панель — интервал снят; у панели любого
    // другого сценария его не было вовсе.
    //
    // Доставка команды площадке (чек, отмена, закрытие) тоже без события: пока она в работе, список
    // перечитывается каждые ~3 с, чтобы трейдер видел «попытка N из 4» вживую, и перестаёт, когда статус конечный.
    const completionPollMs = completionListPollMs(myPayments.map(payment => payment.externalCompletion));
    const signalPolling = shouldPollClientSignals(selectedPayment);
    const pollIntervalMs = completionPollMs === null
        ? EXCHANGE_SIGNAL_POLL_MS
        : signalPolling ? Math.min(completionPollMs, EXCHANGE_SIGNAL_POLL_MS) : completionPollMs;
    const pollingClientSignals = signalPolling || completionPollMs !== null;
    useEffect(() => {
        if (!token || !pollingClientSignals) return;
        const intervalId = window.setInterval(() => {
            void refreshMyPayments();
        }, pollIntervalMs);
        return () => window.clearInterval(intervalId);
    }, [token, pollingClientSignals, pollIntervalMs, refreshMyPayments]);

    const handleClaim = useCallback(async (orderId: string) => {
        if (!token) {
            setError("Требуется повторный вход.");
            return;
        }

        setError("");
        setNotice("");
        setClaimingOrderIds((prev) => new Set(prev).add(orderId));
        try {
            const claimed = await claimOrder(orderId, token);
            setMyPayments((prev) => upsertPayment(prev, claimed));
            setHiddenOrderIds((prev) => new Set(prev).add(orderId));
            getMyPayments(token)
                .then(items => setMyPayments(previous => mergePaymentSnapshots(previous, items)))
                .catch(() => setError("Заявка взята, но список не обновился. Нажмите «Обновить мои платежи»."));
        } catch (err) {
            if (err instanceof ApiError && err.status === 409) {
                const code = apiErrorCode(err);
                if (code === "exchange_sell_address_unavailable") {
                    setError("Нет доступного TRC20-адреса для этой заявки. Попросите администратора добавить адрес и привязать его к вам, затем повторите Take.");
                    return;
                }
                // Claim отдаёт несколько разных 409, и различает их код в теле, а не
                // текст. Заявка мерчанта на предоплате остаётся в пуле: она станет
                // доступной сразу после пополнения, и WS пришлёт свежий снапшот.
                if (code === "merchant_prepay_insufficient") {
                    setError("У мерчанта не хватает предоплаченного баланса на эту заявку.");
                    return;
                }
                // Собственный срок заявки вышел. Строку прячем: заявка закроется
                // сама, повторное нажатие ничего не изменит.
                if (code === "order_deadline_too_close") {
                    setError("Срок заявки вышел, взять её уже нельзя.");
                    setHiddenOrderIds((prev) => new Set(prev).add(orderId));
                    return;
                }
                // Заявку уже взяли (гоночный claim) — прячем строку из пула.
                setError("Order already claimed.");
                setHiddenOrderIds((prev) => new Set(prev).add(orderId));
                return;
            }
            setError(formatApiError(err));
        } finally {
            setClaimingOrderIds((prev) => {
                const next = new Set(prev);
                next.delete(orderId);
                return next;
            });
        }
    }, [token]);

    const handleOpenPayment = useCallback((payment: Payment) => {
        setError("");
        setNotice("");
        setSelectedPaymentKey(paymentKey(payment));
    }, []);

    const handlePaymentSubmit = useCallback(async (request: PaymentActionRequest) => {
        if (!token) {
            throw new ApiError(401, "Authentication is required.");
        }
        const {action, payment} = request;
        const paymentId = payment.paymentId;
        if (!paymentId) {
            throw new ApiError(400, "Bad request", "payment id is missing");
        }

        if (action === "confirmPart") {
            const part = request.payinPartConfirmation;
            if (!part) throw new ApiError(400, "Part confirmation is required");
            await confirmPayinPart(token, paymentId, part.partNumber, part.requisitesVersion, part.receiptId, false, part.partRevision);
            await refreshMyPayments();
            return;
        }
        if (action === "replacePart") {
            const replacement = request.payinPartReplacement;
            if (!replacement) throw new ApiError(400, "Part replacement is required");
            await replacePayinPart(token, paymentId, replacement.partNumber, replacement);
            await refreshMyPayments();
            return;
        }
        if (action === "setRequisites") {
            if (!request.payinRequisites) {
                throw new ApiError(400, "Bad request", "pay-in requisites are missing");
            }
            await setPayinRequisites(
                paymentId,
                token,
                request.payinOrderAmount === undefined
                    ? request.payinRequisites
                    : {...request.payinRequisites, orderAmount: request.payinOrderAmount},
            );
            setError("");
            setNotice(formatActionSuccess(action, payment.status));
            await refreshMyPayments();
            return;
        }

        let externalCompletion = payment.externalCompletion;
        let completionReadFailed = false;
        let status: string;
        if (action === "complete") {
            const receiptIds = await uploadChosenReceipts(paymentId, token, request);
            const result = await completePayment(paymentId, token, {
                fiatAmountReal: request.fiatAmountReal ?? 0,
                currencyRate: request.currencyRate ?? "",
                // Обычной заявке список не отправляется вовсе (В-24): у неё чек
                // один, и сервер теперь отвергает всё, что больше.
                receiptIds:
                    request.presentReceiptIds && receiptIds.length > 0 ? receiptIds : undefined,
            });
            status = result.status;
            if (payment.workflowKind === "external_payout") {
                try {
                    const sync = await apiFetch<{completion: Payment["externalCompletion"]}>(`/payments/${paymentId}/external-completion`, token);
                    externalCompletion = sync.completion;
                } catch {
                    completionReadFailed = true;
                }
            }
        } else if (action === "cancelOrder") {
            // Сначала файлы, потом отмена — и это не удобство, а условие: сервер
            // отменяет заявку только по платежу, у которого есть загруженный
            // файл-пруф. Список ему не отправляется: при отмене он спрашивает не
            // «какими закрыли», а «есть ли хоть один».
            await uploadChosenReceipts(paymentId, token, request);
            const result = await cancelOrderByTrader(paymentId, token, {
                requisiteLast4: request.requisiteLast4 ?? "",
                fiatAmount: request.fiatAmount ?? 0,
            });
            status = result.status;
            // Как и после завершения: сразу читаем доставку отмены, иначе строка
            // исчезла бы из списка и вернулась со следующим снимком, а трейдер
            // за это время нажал бы «Отмена» ещё раз.
            if (payment.workflowKind === "external_payout") {
                try {
                    const sync = await apiFetch<{completion: Payment["externalCompletion"]}>(`/payments/${paymentId}/external-completion`, token);
                    externalCompletion = sync.completion;
                } catch {
                    completionReadFailed = true;
                }
            }
        } else if (action === "cancel") {
            status = (await cancelPayment(paymentId, token)).status;
        } else if (action === "payoutSent") {
            // Ответ — карточка заявки, а не платежа: этой ручкой платёж не
            // меняется вовсе, и статус берётся из неё же, чтобы правило
            // активности читало ровно то, что вернул сервер.
            const card = await sendExchangePayout(paymentId, token, {txHash: request.txHash ?? ""});
            setMyPayments((prev) => mergePaymentSnapshots(prev, prev.map((item) => (paymentKey(item) === paymentKey(payment) ? card : item))));
            setError("");
            // Закрылся этап или только записалась отправка — решает ОТВЕТ
            // сервера, а не наша догадка о режиме: при включённом
            // автоподтверждении перевод подтверждает сеть, и говорить трейдеру
            // «сделка завершена» в этот момент было бы неправдой.
            const stageClosed = card.exchange?.payoutStatus === "confirmed";
            setNotice(stageClosed
                ? "USDT-этап закрыт, сделка завершена."
                : "Отправка записана. Этап закроется, когда перевод подтвердит сеть.");
            // Панель закрывается только вместе с этапом: пока он открыт,
            // трейдеру есть на что смотреть — состояние «ждём сеть» и таймер.
            if (stageClosed) setSelectedPaymentKey(null);
            void refreshMyPayments();
            return;
        } else {
            status = (await appealPayment(paymentId, token)).status;
        }

        // Server response is the source of truth. success/cancelled leave the
        // active list; appeal stays active until it is resolved elsewhere.
        setError("");
        setNotice(completionReadFailed ? "Локальный ответ получен. Обновите список для проверки подтверждения Paylord." : externalCompletion ? completionLabel(externalCompletion) : formatActionSuccess(action, status));
        // Скрытие снимается ТОЛЬКО у возврата в пул, и это не мелочь.
        //
        // `hiddenOrderIds` прячет заявку из пула локально, не дожидаясь снимка от
        // сервера. Возврат в пул — единственный случай, когда заявка после
        // действия там действительно есть, и прятать её больше не от чего.
        //
        // У успеха и терминальной отмены заявка мертва. Снятое скрытие
        // воскрешало её на экране: платёж ушёл из активных, скрывать стало
        // нечем, и старая запись из WS-снимка снова рисовалась в пуле — с
        // живой кнопкой Take по заявке, которой больше нет. Ждать, пока снимок
        // придёт сам, тоже нельзя: он приходит по событию, а не по таймеру.
        if (action === "cancel") {
            setHiddenOrderIds((prev) => {
                const next = new Set(prev);
                next.delete(payment.orderId);
                return next;
            });
        }

        // Скрытие после действия спрашивает то же правило, что и фильтр списка,
        // а не голый статус. У покупки USDT успех рублей работу не заканчивает:
        // открывается второй этап, и убрать строку здесь значило бы спрятать от
        // трейдера перевод, который он ещё должен сделать. Статус берётся из
        // ответа сервера, остальная карточка — из текущего снимка.
        const afterAction = {...payment, status: status as Payment["status"], externalCompletion};
        // Keep the server-confirmed status even when the row leaves the active
        // view, so an older in-flight poll cannot bring it back.
        setMyPayments(prev => prev.map(item => paymentKey(item) === paymentKey(payment) ? {...item, status: afterAction.status, externalCompletion: afterAction.externalCompletion} : item));
        if (!isActiveTraderPayment(afterAction)) {
            setSelectedPaymentKey(null);
        } else if (!drawerStaysOpen(afterAction)) {
            // appeal is non-terminal: keep the row active, but close the drawer
            // so the submit button does not stay in its pending state.
            //
            // Успех рублей по заявке-обмену — единственное исключение, и оно
            // здесь же: работа продолжается на втором этапе, и закрыть карточку
            // значило бы увести трейдера от неё вместе с рублёвой частью,
            // которую он потом не откроет ниоткуда.
            setSelectedPaymentKey(null);
        }
        // A terminal payment no longer means the order left this trader. An
        // external order keeps its claim: the partial close creates the next
        // pending payment in the same transaction (EXT-36), and that payment
        // reaches the list through no other route — the websocket publishes the
        // free pool, from which such an order is hidden on purpose. Without this
        // re-read the order vanishes from the screen until a manual refresh.
        // The row above is hidden by the active filter so the list does not lag,
        // and for an ordinary order this is a plain re-read of an unchanged list.
        void refreshMyPayments();
    }, [token, refreshMyPayments]);

    return (
        <div className="page payments-page">
            <div className="page-header">
                <h2>Payments</h2>
                <button className="btn btn-ghost btn-sm" onClick={refreshMyPayments} disabled={loadingMyPayments}>
                    {loadingMyPayments ? "Refreshing..." : "Refresh my payments"}
                </button>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            {notice && <div className="alert alert-info">{notice}</div>}
            <div className="payments-layout-grid">
                <div className="payments-main-column">
            <PaymentsSplitView
                        availablePayments={availablePayments}
                        myPayments={workingMyPayments}
                        stalledPayments={stalledMyPayments}
                        claimingOrderIds={claimingOrderIds}
                        selectedPaymentKey={selectedPaymentKey}
                        onClaim={handleClaim}
                        onOpenPayment={handleOpenPayment}
                        onExpire={refreshMyPayments}
                    />
                </div>
                <div className="payments-side-column">
                {/* Только сумма баланса команды. Померчантной разбивки здесь
                    больше нет (решение владельца 01.09.2026): в ЛК трейдера
                    деление по мерчантам не показывается нигде, оно осталось
                    внутренним — снапшот вывода режется по срезам и гасится по
                    ним же. Ниже, в виджете курса, «By merchant» — про наценки
                    мерчантов, а не про балансы. */}
                <aside className="balance-preview">
                    <div className="section-header">
                        <div>
                            <h3>Balance preview</h3>
                            <span>Team balance (read-only)</span>
                        </div>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={refreshBalance}
                            disabled={balanceLoading}
                        >
                            {balanceLoading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                    <div className="balance-preview-body">
                        {balanceError && (
                            <div className="alert alert-error">{balanceError}</div>
                        )}
                        {balanceMissing && (
                            <div className="balance-line">
                                <span>Team balance</span>
                                <strong>Not available</strong>
                            </div>
                        )}
                        {balance && (
                            <>
                                <div className="balance-line">
                                    <span>Team balance</span>
                                    <strong className="mono" title={`${balance.amount} ${balance.currency}`}>
                                        {truncateAmount(balance.amount)} {balance.currency}
                                    </strong>
                                </div>
                                <div className="balance-line">
                                    <span>Last reset</span>
                                    <strong className="mono" title={balance.lastBalanceResetDate || undefined}>
                                        {formatRelativeTime(balance.lastBalanceResetDate)}
                                    </strong>
                                </div>
                            </>
                        )}
                        {!balance && !balanceMissing && !balanceError && balanceLoading && (
                            <div className="balance-line">
                                <span>Loading...</span>
                            </div>
                        )}
                    </div>
                </aside>
                {(rapiraRate?.enabled === true || (rapiraRate === null && Boolean(rapiraRateError))) && (
                    <aside className="balance-preview rapira-rate-preview">
                        <div className="section-header">
                            <div>
                                <h3>Rapira rate</h3>
                                <span>{rapiraRateLoading ? "Refreshing..." : "Exchange rate (read-only)"}</span>
                            </div>
                        </div>
                        <div className="balance-preview-body">
                            {rapiraRateError && (
                                <div className="alert alert-error">{rapiraRateError}</div>
                            )}
                            {rapiraRate?.enabled === true && (
                                <>
                                    {/* Устаревшая котировка остаётся на экране: трейдеру
                                        важнее знать, что курс перестал обновляться, чем
                                        увидеть пустой виджет. Признак приходит с бэка. */}
                                    {rapiraRate.stale && (
                                        <div className="alert alert-error rapira-rate-stale">
                                            Рапира недоступна, курс устарел — учтите.
                                        </div>
                                    )}
                                    <div className="balance-line">
                                        <span>Rapira</span>
                                        <strong
                                            className={`mono${rapiraRate.stale ? " stale" : ""}`}
                                            title={rapiraRate.quotedAt
                                                ? `${rapiraRate.baseRate ?? "Курс недоступен"} · ${rapiraRate.quotedAt}`
                                                : rapiraRate.baseRate ?? undefined}
                                        >
                                            {rapiraRate.baseRate ?? "Курс недоступен"}
                                        </strong>
                                    </div>
                                    {rapiraRate.quotedAt && (
                                        <div className="balance-line balance-line-compact">
                                            <span>Quoted</span>
                                            <strong className={`mono${rapiraRate.stale ? " stale" : ""}`} title={rapiraRate.quotedAt}>
                                                {formatAgeSeconds(rapiraRate.ageSeconds)}
                                            </strong>
                                        </div>
                                    )}
                                    <div className="balance-preview-by-merchant">
                                        <div className="balance-preview-subheading">By merchant</div>
                                        {rapiraRate.merchants.length > 0 ? (
                                            rapiraRate.merchants.map((merchant) => (
                                                <div className="balance-line balance-line-compact" key={merchant.merchantId}>
                                                    <span>{merchant.merchantName} ({formatRateMarkup(merchant.markupPercent)})</span>
                                                    <strong className="mono" title={merchant.rate ?? undefined}>
                                                        {merchant.rate ?? "—"}
                                                    </strong>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="balance-line balance-line-compact">
                                                <span className="muted">Нет Rapira-мерчантов</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </aside>
                )}
            </div>
            </div>
            {selectedPayment && (
                <PaymentDetailsPanel
                    payment={selectedPayment}
                    onClose={() => setSelectedPaymentKey(null)}
                    onSubmit={handlePaymentSubmit}
                    onExpire={refreshMyPayments}
                />
            )}
        </div>
    );
}

function upsertPayment(items: Payment[], payment: Payment): Payment[] {
    const key = payment.paymentId ?? payment.orderId;
    const withoutCurrent = items.filter((item) => (item.paymentId ?? item.orderId) !== key);
    return [payment, ...withoutCurrent];
}

function formatApiError(err: unknown): string {
    if (err instanceof ApiError) {
        if (err.status === 401) return "Authentication is required.";
        if (err.status === 403) return "You do not have permission to claim this order.";
        return err.message;
    }
    if (err instanceof Error) return err.message;
    return "Unexpected request error.";
}

function paymentKey(payment: Payment): string {
    return payment.paymentId ?? payment.orderId;
}

function compareByOrderAge(a: Payment, b: Payment): number {
    const ageA = getOrderCreatedAtMs(a);
    const ageB = getOrderCreatedAtMs(b);
    if (ageA !== ageB) return ageA - ageB;
    return a.orderId.localeCompare(b.orderId);
}

function getOrderCreatedAtMs(payment: Payment): number {
    const parsed = Date.parse(payment.orderCreatedAt || payment.createdAt);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

// uploadChosenReceipts заливает выбранные трейдером файлы и отдаёт их id в
// порядке выбора.
//
// Один и тот же цикл на закрытие и на отмену намеренно: другого способа
// загрузить файл в системе нет, а «нельзя отменить без прикреплённого файла»
// без него означало бы «нельзя отменить никогда». Уже загруженный файл (у
// драфта есть receiptId) второй раз не заливается — повтор после отказа иначе
// плодил бы у одного перевода несколько строк в `receipts`.
//
// Обрыв на середине пачки останавливает всё действие и говорит, что доехало:
// закрыть платёж половиной доказательств нельзя, а отменить заявку — тем более.
// Выбранные файлы остаются в панели, загруженные помнят свой id, и повтор
// догружает только недостающее.
async function uploadChosenReceipts(
    paymentId: string,
    token: string,
    request: PaymentActionRequest
): Promise<string[]> {
    const receiptIds: string[] = [];
    for (const {file, receiptId} of request.receipts ?? []) {
        if (receiptId) {
            receiptIds.push(receiptId);
            continue;
        }
        try {
            const uploaded = await uploadReceipt(paymentId, file, token);
            receiptIds.push(uploaded.receiptId);
            request.onReceiptUploaded?.(file, uploaded.receiptId);
        } catch {
            const done = receiptIds.length;
            throw new Error(
                done === 0
                    ? `Не удалось загрузить файл «${file.name}». Действие не выполнено, попробуйте ещё раз.`
                    : `Загружено файлов: ${done}. На «${file.name}» загрузка прервалась — действие не выполнено, файлы на месте, просто повторите отправку.`
            );
        }
    }
    return Array.from(new Set(receiptIds));
}

function formatActionSuccess(action: PaymentActionKind, status: string): string {
    if (action === "setRequisites") {
        return "Реквизиты приёма сохранены.";
    }
    if (action === "complete") {
        return status.toLowerCase() === "success"
            ? "Платёж проведён, список обновлён."
            : "Платёж отправлен.";
    }
    // У «Вернуть в пул» слова «отмена» нет: трейдер ничего не отменяет, он
    // отпускает заявку. Внутренняя строка платежа закрывается, но трейдеру про
    // неё знать нечего — он видит ровно то, что произошло с заявкой.
    if (action === "cancel") return "Заявка вернулась в пул и доступна другим трейдерам.";
    // А здесь слово честное: заявка мертва, и мерчант об этом уже уведомлен.
    if (action === "cancelOrder") return "Заявка отменена, мерчант уведомлён.";
    // Заявка закрыта целиком только сейчас: у покупки USDT успех рублей был
    // половиной работы, и сказать «готово» раньше значило бы соврать.
    if (action === "payoutSent") return "USDT-этап закрыт, сделка завершена.";
    return "Спор открыт.";
}
